import Button from '@/common/components/Button'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import GoBackButton from '@/common/components/GoBackButton'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useCreateTmaisBadge } from '@/features/tmaisBadges/hooks/useCreateTmaisBadge'
import { TmaisBadgeForm } from '@/features/tmaisBadges/components/TmaisBadgeForm'
import { createTmaisBadgeSchema, TmaisBadgeFormSchema } from '@/features/tmaisBadges/schemas/form.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronsRight } from 'lucide-react'
import { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { useGoBack } from '@/common/hooks/useGoBack' 

const CreateTmaisBadge = () => {
  const goBack = useGoBack() 
  const queryClient = useQueryClient()
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const canCreate = isUserPermissionsPending || hasUserPermission('Admin::Tmais::Badges::Create')

  const form = useForm<TmaisBadgeFormSchema>({
    resolver: zodResolver(createTmaisBadgeSchema),
    defaultValues: {
      display_name: '',
      background_color: '#000000',
      text_color: '#FFFFFF',
      icon: null,
    },
  })

  const { handleSubmit, setError } = form

  const { mutate: createBadge, isPending } = useCreateTmaisBadge({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tmaisBadges'] })
      goBack({ fallback: '/tmais-badges' })
    },
    onError: FormTransformers.errorsTransformer({ setError, setMessages }),
  })

  const onSubmit: SubmitHandler<TmaisBadgeFormSchema> = (values) => createBadge(values)

  return (
    <ErrorMessageWrapper className="h-[calc(100vh-128px)]" statusCode={canCreate ? 200 : 403}>
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off" className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GoBackButton fallback="/tmais-badges" />
            <p className="text-white-400 font-normal">Destaques para itens</p>
            <ChevronsRight className="text-white-400" />
            <h2>Cadastrar destaque</h2>
          </div>

          <div className="flex gap-4">
            <Button 
              variant="outline-error" 
              type="button"
              onClick={() => goBack({ fallback: '/tmais-badges' })}
            >
              Cancelar
            </Button>

            <Button
              type="submit"
              className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
              isLoading={isPending}
              disabled={isPending}
              operationKlassToCheckPermission="Admin::Tmais::Badges::Create"
            >
              Cadastrar destaque
            </Button>
          </div>
        </div>

        <TmaisBadgeForm form={form} isSubmitting={isPending} iconRequired />

        {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
      </form>
    </ErrorMessageWrapper>
  )
}

export default CreateTmaisBadge